import { apiKeyManager } from '@/lib/apiKeyManager'
import { demoModeManager } from '@/lib/demoModeManager'

interface SmartleadConfig {
  baseUrl: string
  rateLimit: {
    requests: number
    windowMs: number
  }
}

interface Campaign {
  id: string
  name: string
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'DRAFTED'
  total_leads: number
  reply_rate: number
  positive_reply_rate: number
  meetings_booked: number
  meetings_qualified: number
  client_id?: number
  category?: string
}

interface CreateCampaignParams {
  name: string;
  client_id?: number;
  sender_id?: string;
  leads?: string[];
  sequence?: EmailSequence[];
  timezone?: string;
  daily_limit?: number;
  schedule?: {
    start_time: string;
    end_time: string;
  };
  // Campaign settings - include all settings in creation
  track_settings?: string[];
  stop_lead_settings?: string;
  send_as_plain_text?: boolean;
  follow_up_percentage?: number;
  enable_ai_esp_matching?: boolean;
  auto_pause_domain_leads_on_reply?: boolean;
  bounce_autopause_threshold?: string;
  force_plain_text?: boolean;
  ignore_ss_mailbox_sending_limit?: boolean;
  unsubscribe_text?: string;
  out_of_office_detection_settings?: {
    ai_categorisation_options?: number[];
  };
}

interface UpdateCampaignScheduleParams {
  timezone?: string;
  days_of_the_week?: number[];
  start_hour?: string;
  end_hour?: string;
  min_time_btw_emails?: number;
  max_new_leads_per_day?: number;
  schedule_start_time?: string;
}

interface UpdateCampaignSettingsParams {
  track_settings?: string[];
  stop_lead_settings?: string;
  unsubscribe_text?: string;
  send_as_plain_text?: boolean;
  follow_up_percentage?: number;
  client_id?: number;
  enable_ai_esp_matching?: boolean;
  name?: string;
  force_plain_text?: boolean;
  auto_pause_domain_leads_on_reply?: boolean;
  ignore_ss_mailbox_sending_limit?: boolean;
  bounce_autopause_threshold?: string;
  out_of_office_detection_settings?: {
    ai_categorisation_options?: number[];
  };
}

interface EmailSequence {
  subject: string;
  body: string;
  delay_days: number;
}

interface CampaignAnalytics {
  total_leads: number
  reply_count: number
  positive_reply_count: number
  meetings_booked: number
  meetings_qualified: number
  sent_count: number
}

interface Mailbox {
  id: string
  email: string
  is_smtp_success: boolean
  is_imap_success: boolean
  daily_sent_count: number
  daily_limit: number
}

interface Client {
  id: number
  name: string
  email: string
  status: string
}

class RateLimitManager {
  private requests: number[] = []
  
  constructor(
    private maxRequests: number,
    private windowMs: number
  ) {}

  async executeWithRateLimit<T>(fn: () => Promise<T>): Promise<T> {
    const now = Date.now()
    this.requests = this.requests.filter(time => now - time < this.windowMs)
    
    if (this.requests.length >= this.maxRequests) {
      const oldestRequest = Math.min(...this.requests)
      const waitTime = this.windowMs - (now - oldestRequest)
      await new Promise(resolve => setTimeout(resolve, waitTime))
    }
    
    this.requests.push(Date.now())
    return fn()
  }
}

class SmartleadAPI {
  private config: SmartleadConfig
  private rateLimiter: RateLimitManager
  // Global cache for fetchCampaigns to prevent multiple simultaneous API calls
  private campaignsCache: { data: Campaign[] | null; timestamp: number; pending: Promise<Campaign[]> | null } = {
    data: null,
    timestamp: 0,
    pending: null
  }
  private static CAMPAIGNS_CACHE_TTL = 60000 // 60 second cache TTL

  constructor() {
    this.config = {
      baseUrl: 'https://server.smartlead.ai/api/v1',
      rateLimit: {
        requests: 8,
        windowMs: 2000
      }
    }
    
    this.rateLimiter = new RateLimitManager(
      this.config.rateLimit.requests,
      this.config.rateLimit.windowMs
    )
  }

  private getApiKey(): string {
    const apiKey = apiKeyManager.getApiKey()
    
    if (!apiKey) {
      console.error('🔑 No API key found - check localStorage and environment')
      throw new Error('Smartlead API key not configured. Please set your API key in the dashboard.')
    }
    
    return apiKey
  }

  async makeRequest<T>(endpoint: string): Promise<T> {
    const apiKey = this.getApiKey()

    return this.rateLimiter.executeWithRateLimit(async () => {
      const url = `${this.config.baseUrl}${endpoint}${endpoint.includes('?') ? '&' : '?'}api_key=${apiKey}`
      
      const response = await fetch(url)

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          apiKeyManager.clearApiKey()
          throw new Error('Invalid API key - please check your Smartlead API key')
        }
        throw new Error(`Smartlead API error: ${response.status} ${response.statusText}`)
      }

      return await response.json()
    })
  }

  async makePostRequest<T>(endpoint: string, data: any): Promise<T> {
    const apiKey = this.getApiKey()

    return this.rateLimiter.executeWithRateLimit(async () => {
      const url = `${this.config.baseUrl}${endpoint}${endpoint.includes('?') ? '&' : '?'}api_key=${apiKey}`
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          apiKeyManager.clearApiKey()
          throw new Error('Invalid API key - please check your Smartlead API key')
        }
        const errorText = await response.text()
        throw new Error(`Smartlead API error: ${response.status} ${response.statusText} - ${errorText}`)
      }

      return await response.json()
    })
  }

  async fetchCampaignAnalytics(campaignId: string): Promise<CampaignAnalytics> {
    return this.makeRequest<CampaignAnalytics>(`/campaigns/${campaignId}/analytics`)
  }

  async fetchCampaigns(): Promise<Campaign[]> {
    // Return cached data if still fresh
    const now = Date.now()
    if (this.campaignsCache.data && (now - this.campaignsCache.timestamp) < SmartleadAPI.CAMPAIGNS_CACHE_TTL) {
      return this.campaignsCache.data
    }

    // Deduplicate in-flight requests
    if (this.campaignsCache.pending) {
      return this.campaignsCache.pending
    }

    const fetchPromise = (async () => {
      try {
        const campaigns = await this.makeRequest<Campaign[]>('/campaigns')
        const result = campaigns.map(campaign => ({
          ...campaign,
          category: this.categorizeCampaign(campaign.name)
        }))
        this.campaignsCache.data = result
        this.campaignsCache.timestamp = Date.now()
        return result
      } finally {
        this.campaignsCache.pending = null
      }
    })()

    this.campaignsCache.pending = fetchPromise
    return fetchPromise
  }

  private categorizeCampaign(name: string): string {
    const lowerName = name.toLowerCase()
    
    // Check for bulk campaigns
    if (lowerName.includes('bulk')) {
      return 'bulk'
    }
    
    // Check for ad-hoc campaigns
    if (lowerName.includes('ad-hoc') || lowerName.includes('adhoc') || lowerName.includes('one-off')) {
      return 'adhoc'
    }
    
    // Check for event campaigns
    if (lowerName.includes('event') || lowerName.includes('webinar') || lowerName.includes('conference')) {
      return 'event'
    }
    
    // Check for trigger campaigns
    if (lowerName.includes('trigger') || lowerName.includes('drip') || lowerName.includes('sequence')) {
      return 'trigger'
    }
    
    // Default to other
    return 'other'
  }

  async fetchMailboxes(): Promise<Mailbox[]> {
    const allMailboxes = []
    let offset = 0
    const limit = 100 // Smartlead's max per request
    let batchCount = 0
    
    console.log('🔄 Starting to fetch all mailboxes with pagination...')
    
    const apiKey = this.getApiKey()
    
    while (true) {
      batchCount++
      console.log(`📧 Fetching mailbox batch ${batchCount} (offset: ${offset}, limit: ${limit})...`)
      
      try {
        // Use the exact endpoint that works with pagination
        const url = `${this.config.baseUrl}/email-accounts?offset=${offset}&limit=${limit}&api_key=${apiKey}`
        console.log(`🔗 Mailbox API URL: ${url}`)
        
        const response = await fetch(url)
        
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            console.error('🔑 API Key authentication failed during mailbox fetch')
            apiKeyManager.clearApiKey()
            throw new Error('Invalid API key - please check your Smartlead API key')
          }
          console.error(`❌ Mailbox API failed: ${response.status} ${response.statusText}`)
          const errorText = await response.text()
          console.error(`❌ Error details: ${errorText}`)
          break
        }
        
        const batch = await response.json()
        console.log(`📦 Received batch ${batchCount}: ${batch.length} mailboxes`)
        
        // If no results or empty array, we've reached the end
        if (!batch || !Array.isArray(batch) || batch.length === 0) {
          console.log(`✅ No more mailboxes found. Finished at batch ${batchCount - 1}`)
          break
        }
        
        // Add this batch to our collection
        allMailboxes.push(...batch)
        console.log(`📊 Batch ${batchCount}: Added ${batch.length} mailboxes. Total so far: ${allMailboxes.length}`)
        
        // If we got fewer results than the limit, we've reached the last page
        if (batch.length < limit) {
          console.log(`🏁 Last batch contained ${batch.length} mailboxes. Pagination complete.`)
          break
        }
        
        // Prepare for next iteration
        offset += limit
        
        // CRITICAL: Rate limiting delay between batches
        if (allMailboxes.length < 1000) { // Only delay if we expect more batches
          console.log(`⏱️ Waiting 2s before next batch for rate limiting...`)
          await new Promise(resolve => setTimeout(resolve, 2000))
        }
        
      } catch (error) {
        console.error(`💥 Error fetching mailbox batch ${batchCount}:`, error)
        
        // Don't break on single batch failure - try to continue
        if (batchCount === 1) {
          // If first batch fails, re-throw the error
          throw error
        } else {
          // If later batch fails, log warning but continue with what we have
          console.warn(`⚠️ Skipping failed batch ${batchCount}, continuing with ${allMailboxes.length} mailboxes`)
          break
        }
      }
    }
    
    return allMailboxes
  }

  async fetchClients(): Promise<Client[]> {
    return this.makeRequest<Client[]>('/client/')
  }

  isConfigured(): boolean {
    if (demoModeManager.isDemoModeForced()) return false
    return apiKeyManager.isValid()
  }

  async testConnection(): Promise<boolean> {
    try {
      const apiKey = this.getApiKey()
      const response = await fetch(
        `${this.config.baseUrl}/campaigns?api_key=${apiKey}`
      )
      
      if (response.ok) {
        return true
      } else {
        if (response.status === 401 || response.status === 403) {
          apiKeyManager.clearApiKey()
        }
        return false
      }
    } catch (error) {
      return false
    }
  }

  async createCampaign(params: CreateCampaignParams): Promise<{ id: string; status: string; details: any }> {
    try {
      
      // Validate required parameters
      if (!params.name) {
        throw new Error('Campaign name is required')
      }

      // Create the campaign payload based on the API documentation
      const campaignData = {
        name: params.name,
        client_id: params.client_id || null
      }

      return await this.makePostRequest<{ id: string; status: string; details: any }>('/campaigns/create', campaignData)
    } catch (error) {
      console.error('❌ Failed to create campaign:', error)
      throw error
    }
  }

  async updateCampaignSchedule(campaignId: string, params: UpdateCampaignScheduleParams): Promise<any> {
    try {
      console.log(`Updating schedule for campaign ${campaignId} with params:`, params)
      
      // Use the correct endpoint for updating campaign schedule
      const apiKey = this.getApiKey()
      const url = `${this.config.baseUrl}/campaigns/${campaignId}?api_key=${apiKey}`
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params)
      })
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Smartlead API error: ${response.status} ${response.statusText} - ${errorText}`)
      }
      
      const result = await response.json()
      console.log('✅ Campaign schedule updated successfully:', result)
      return result
    } catch (error) {
      console.error('❌ Failed to update campaign schedule:', error)
      throw error
    }
  }

  async updateCampaignSettings(campaignId: string, params: UpdateCampaignSettingsParams): Promise<any> {
    try {
      console.log(`Updating settings for campaign ${campaignId} with params:`, params)
      
      const result = await this.makePostRequest<any>(`/campaigns/${campaignId}/settings`, params)
      
      console.log('✅ Campaign settings updated successfully:', result)
      return result
    } catch (error) {
      console.error('❌ Failed to update campaign settings:', error)
      throw error
    }
  }
}

export const smartleadAPI = new SmartleadAPI()
export type { Campaign, Mailbox, Client, CampaignAnalytics, CreateCampaignParams, EmailSequence, UpdateCampaignScheduleParams, UpdateCampaignSettingsParams }
